import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Car, LogIn, Loader2 } from "lucide-react";
import { toast } from "sonner";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await login(email, password);
      toast.success("Welkom terug!");
      navigate("/");
    } catch (error) {
      const message = error.response?.data?.detail || "Inloggen mislukt";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1729843606560-e41b0be7fc7c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxhdXRvJTIwbWVjaGFuaWMlMjB1bmRlciUyMGNhciUyMGxpZnQlMjByZXBhaXJpbmclMjB2ZWhpY2xlfGVufDB8fHx8MTc3MTIyODQ0MXww&ixlib=rb-4.1.0&q=85')`
        }}
      />
      
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-mechanic-blue/90 via-mechanic-blue/80 to-black/70" />
      
      {/* Content */}
      <div className="relative min-h-screen flex flex-col items-center justify-center p-4">
        {/* Logo */}
        <div className="mb-8 text-center animate-slide-up">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-safety-orange shadow-2xl shadow-orange-500/30 mb-4">
            <Car className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-heading text-4xl font-black text-white tracking-tight">
            Rally Auto Advies
          </h1>
          <p className="text-white/70 mt-2 font-medium">
            Voertuigkeuring Systeem
          </p>
        </div>

        {/* Login Card */}
        <Card className="w-full max-w-md shadow-2xl animate-slide-up" style={{ animationDelay: '100ms' }} data-testid="login-card">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="font-heading text-2xl text-center text-primary">
              Inloggen
            </CardTitle>
            <CardDescription className="text-center">
              Voer je gegevens in om verder te gaan
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-mailadres</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="naam@bedrijf.nl"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12"
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Wachtwoord</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12"
                  data-testid="login-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-12 bg-safety-orange hover:bg-safety-orange-dark text-white font-bold shadow-lg shadow-orange-500/30 active:scale-95 transition-all"
                disabled={isLoading}
                data-testid="login-submit-button"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <LogIn className="w-5 h-5 mr-2" />
                )}
                Inloggen
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="mt-8 text-white/50 text-sm animate-slide-up" style={{ animationDelay: '200ms' }}>
          Rally Auto Advies v1.0 - Professionele Voertuigkeuring
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
